<template>
  <div class="page-companies">
    <PageHeader>
      <h1 v-if="recentOnly">
        Companies Created in {{currentMonth}} <span>({{ recentCompanyCount }})</span>
      </h1>

      <h1 v-else>
        All Companies <span>({{ totalCompanies }})</span>
      </h1>

      <Btn primary hasIcon tooltip="Add Company">
        <router-link :to="{name: 'add-company'}">
          <Sprite name="add" />
        </router-link>
      </Btn>
    </PageHeader>

    <div class="page-columns">
      <div class="col main">
        <div class="page-content">
          <div class="company-filters">
            <form class="company-search" @submit.prevent="searchCompany">
              <div class="input">
                <input v-model="query" placeholder="Search for a company..." />
              </div>
            </form>

            <btn @click="showAllCompanies" v-if="recentOnly" secondary
              >All Companies</btn
            >
            <btn @click="showRecentCompanies" v-if="!recentOnly" secondary
              >Created in {{currentMonth}}</btn
            >
          </div>

          <table-data
            :rows="companies"
            :headings="[
              {heading: 'Company Name', key: 'company-name'},
              {heading: 'Created', key: 'company-created'}
            ]"
            :pagination="pagination"
          >
            <div
              v-for="(company, index) in companies"
              :slot="'td-' + index + '-company-name'"
              :key="index"
            >
              <router-link
                :to="{name: 'company', params: {companyId: company.id}}"
              >
                {{ company.name }}
              </router-link>
            </div>

            <div
              v-for="(company, index) in companies"
              :slot="'td-' + index + '-company-created'"
              :key="index"
            >
              <router-link
                :to="{name: 'websites', params: {companyId: company.id}}"
              >
                {{ createdAt(company.created_at) }}
              </router-link>
            </div>
          </table-data>

          <pagination @changePage="changePage" :pagination="pageData">
          </pagination>
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <div>
            <h1>Global Companies</h1>
            <p>
              A company is automatically created when a user signs up. Here is a
              list of all companies on the platform, you can navigate to each
              company to view their <strong>sites</strong> and
              <strong>company users</strong>.
            </p>
          </div>
        </PageSummary>
      </div>
    </div>

    <router-view />
  </div>
</template>

<script>
import page from '@/mixins/page'
import pagination from '@/mixins/pagination'
import moment from 'moment'

import {readCompaniesData} from '@/lib/companies'

import Table from '@/components/Table'
import PageHeader from '@/components/PageHeader'
import PageSummary from '@/components/PageSummary'
import Sprite from '@/components/Sprite'
import Btn from '@/components/Btn'

export default {
  name: 'companies',

  mixins: [page, pagination],

  components: {
    TableData: Table,
    PageHeader,
    PageSummary,
    Sprite,
    Btn
  },

  data() {
    return {
      companies: [],
      query: '',
      recentOnly: false,
      company: {},
      errors: {},
      title: 'Companies'
    }
  },

  methods: {
    getData() {
      readCompaniesData({page: 1, page_size: 500}).then(({data, page}) => {
        this.pageData = page

        this.companies = data.sort(function(a, b) {
          return new Date(b.created_at) - new Date(a.created_at)
        })
      })
    },

    confirmDelete(company) {
      if (confirm('Are you sure?')) {
        this.deleteCompany(company.id)
      }
    },

    searchCompany() {
      if (this.query.length) {
        let query = this.query.toLowerCase()

        this.companies = this.companies.filter((company) =>
          company.name.toLowerCase().includes(query)
        )
      } else {
        this.getData()
      }
    },

    showRecentCompanies() {
      this.recentOnly = true

      let timeNow = Date.now()
      let thisMonth = moment(timeNow).format('MM')

      this.companies = this.companies.filter(
        (company) => thisMonth === moment(company.created_at).format('MM')
      )
    },

    showAllCompanies() {
      this.recentOnly = false
      this.getData()
    }
  },

  computed: {
    totalCompanies() {
      return this.pageData.total_entries
    },

    createdAt() {
      return (date) => {
        return moment(date).fromNow()
      }
    },

    currentMonth() {
      let timeNow = Date.now()

      return moment(timeNow).format('MMMM')
    },

    recentCompanyCount() {
      return this.companies.length
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    '$route'() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-companies
  @extend %page

  .company-filters
    width: 100%
    display: flex
    align-items: center
    justify-content: space-between
    margin-bottom: $gap-medium

    .company-search
      flex: 1
      padding-right: $gap-medium

      .input
        margin-bottom: 0

    .btn
      font-size: 10px
      min-width: 175px
</style>
